import React, { useState } from 'react';

import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { Alert, Button, Container, Stack, TextField } from '@mui/material';
import { DataGrid, GridColDef, GridRowParams } from '@mui/x-data-grid';

import GridPagination from '~/components/Common/Elements/GridPagination';
import PageHeader from '~/components/Common/Elements/PageHeader';

import { SEARCH_DEBOUNCE_MS } from '~/consts/tours';

import { useGetSuppliers } from '~/hooks/apis/tours/useTourSuppliers';
import useDebounced from '~/hooks/useDebounced';

import { Supplier } from '~/types/tours/supplier';

import { supplierPath, vendorPath } from '../../nativeTourModel';

const PAGE_SIZE = 20;

const columns: Array<GridColDef<Supplier>> = [
  {
    field: 'sourceSupplierId',
    headerName: 'Supplier ID',
    sortable: true,
    disableColumnMenu: true,
    flex: 1,
    display: 'flex',
    valueGetter: (value) => value ?? '—',
  },
  {
    field: 'name',
    headerName: 'Supplier Name',
    sortable: true,
    disableColumnMenu: true,
    flex: 1,
    display: 'flex',
  },
  {
    field: 'vendor',
    headerName: '',
    sortable: false,
    disableColumnMenu: true,
    width: 160,
    align: 'right',
    display: 'flex',
    renderCell: ({ row }) =>
      row.sourceSupplierId ? (
        // Stop the row click opening the supplier page as well.
        <Button
          size="small"
          component={RouterLink}
          to={vendorPath(row.sourceSupplierId)}
          onClick={(event) => event.stopPropagation()}
        >
          View vendor
        </Button>
      ) : null,
  },
];

export default function ToursSuppliersPage() {
  const navigate = useNavigate();
  const [nameInput, setNameInput] = useState('');

  const name = useDebounced(nameInput.trim(), SEARCH_DEBOUNCE_MS);

  const { data: suppliers = [], isLoading, error } = useGetSuppliers(name);

  return (
    <Container>
      <PageHeader title="Suppliers" />

      <Stack spacing={2}>
        <TextField label="Search by name" value={nameInput} onChange={(event) => setNameInput(event.target.value)} />

        {!!error && <Alert severity="error">{error.message}</Alert>}

        {!error && (
          <DataGrid
            rows={suppliers}
            columns={columns}
            loading={isLoading}
            onRowClick={(params: GridRowParams<Supplier>) => navigate(supplierPath(params.row.id))}
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: PAGE_SIZE } },
              sorting: { sortModel: [{ field: 'name', sort: 'asc' }] },
            }}
            pageSizeOptions={[PAGE_SIZE]}
            slots={{ pagination: GridPagination }}
            localeText={{
              noRowsLabel: name
                ? `No suppliers match "${name}".`
                : "No suppliers yet. They're created from the DMC accounts on each tour's Salesforce contracts.",
            }}
            disableColumnMenu
            disableColumnFilter
            disableRowSelectionOnClick
            autoHeight
            sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
          />
        )}
      </Stack>
    </Container>
  );
}
